import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { cn } from "@wellcheck/components/lib/cn";
import { Button } from "@wellcheck/components/ui/button";

import { useQuestionnaire } from "@/features/respond/api/useQuestionnaire";
import { buildLocaleSwitchPath } from "@/i18n/buildLocaleSwitchPath";
import {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  isSupportedLocale,
  type SupportedLocale,
} from "@/i18n/config";

interface RespondentHomeProps {
  onStart: () => void;
}

/**
 * RespondentHome — 回答開始前の導入画面。
 *
 * BJSQ の注意書き (全質問必須・未回答だと結果が不正確) を表示してから回答に入る。
 * 本人確認 UI (氏名・組織・従業員番号下4桁) は後続タスクで追加予定。
 *
 * 回答者がキャンペーン URL (/:lang/r/:campaignId/:respondentId) を開いて最初に見る
 * 画面であるため、言語選択をここに前面化する (方針修正: 当初は HomePage 側に
 * 置いていたが、実際の回答者は HomePage を経由せずここへ直接来るため移設した)。
 * 「回答をはじめる」ボタンと同じ視線の流れで選べるよう、ボタン直上にインライン配置する。
 * ヘッダー右上の LanguageSelector は回答中の切替用として引き続き残す。
 */
export function RespondentHome({ onStart }: RespondentHomeProps) {
  const { t } = useTranslation("respond");
  const { t: tCommon } = useTranslation();
  const { lang } = useParams<{ lang: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const current: SupportedLocale = isSupportedLocale(lang) ? lang : DEFAULT_LOCALE;
  const selectedButtonRef = useRef<HTMLButtonElement>(null);
  // SurveyForm と同じ query key を共有するため、ここでの取得は開始後の再フェッチにならない。
  const questionnaireType = useQuestionnaire().data?.questionnaireType;

  const onSelectLocale = useCallback(
    (next: SupportedLocale) => {
      if (next === current) return;
      const target = buildLocaleSwitchPath(location.pathname, location.search, location.hash, next);
      navigate(target, { replace: true });
    },
    [current, location, navigate],
  );

  // 常に 1 行・横スクロール可能なレイアウト (下記コメント参照) にしたため、
  // 選択中の言語ボタンが横スクロール領域の外に隠れうる。ブラウザの scroll 状態という
  // React 外の世界と同期する必要があるため useEffect を使う (react.md の許容ケース)。
  // jsdom (vitest) には scrollIntoView が実装されていないため存在チェックする。
  useEffect(() => {
    selectedButtonRef.current?.scrollIntoView?.({ inline: "nearest", block: "nearest" });
  }, [current]);

  return (
    <section className="space-y-8">
      <header className="space-y-2">
        <h2 className="text-2xl font-semibold">{t("home.title")}</h2>
        <p className="text-lg leading-relaxed text-muted-foreground">
          {questionnaireType
            ? t("home.descriptionWithCount", { questionCount: questionnaireType })
            : t("home.description")}
        </p>
      </header>
      <ul className="space-y-3 rounded-md border border-border bg-muted/40 p-5 text-lg leading-relaxed">
        <li>{t("home.notice1")}</li>
        <li>{t("home.notice2")}</li>
        <li>{t("home.notice3")}</li>
      </ul>

      <div className="space-y-3 border-t border-border pt-6">
        <p className="text-sm font-medium text-muted-foreground">{t("home.chooseLanguage")}</p>
        {/*
          レイアウトのガタつき防止: 5 言語 (最長ラベルは "Tiếng Việt" / "Português") を
          常に 1 行に固定し、折り返させない。Tailwind の sm: はビューポート幅基準で
          判定されるため、コンテナ幅が狭い状況 (親要素の制約・ズーム等) では
          grid-cols の切り替えが効かず折り返してしまうケースがあった。
          行数や各ボタンの折り返しで高さが変わると直下の「回答をはじめる」ボタンの
          位置がガタつくため、常に 1 行・横スクロール可能な構成にして高さを固定する。
        */}
        <div
          role="group"
          aria-label={tCommon("language.label")}
          className="flex flex-nowrap gap-2 overflow-x-auto pb-1"
        >
          {SUPPORTED_LOCALES.map((locale) => {
            const selected = locale === current;
            return (
              <Button
                key={locale}
                ref={selected ? selectedButtonRef : undefined}
                type="button"
                variant={selected ? "default" : "outline"}
                size="lg"
                lang={locale}
                aria-pressed={selected}
                className={cn("shrink-0 whitespace-nowrap", selected && "pointer-events-none")}
                onClick={() => onSelectLocale(locale)}
              >
                {tCommon(`language.${locale}` as const)}
              </Button>
            );
          })}
        </div>
      </div>

      <div className="flex justify-end border-t border-border pt-6">
        <Button size="lg" className="h-14 px-8 text-lg" onClick={onStart}>
          {t("home.start")}
        </Button>
      </div>
    </section>
  );
}
